import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Avatar,
  Rating,
} from '@mui/material';
import {
  LocationOn,
  AccessTime,
  Star,
  TrendingUp,
  People,
  Flight,
  ChevronLeft,
  ChevronRight,
  CalendarToday,
  Person,
  Security,
  AttachMoney,
  Support,
  Tour,
  FormatQuote,
  Phone,
  Email
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { tourService, blogService, homePageService } from '../services/firebase';
import { Tour as TourType, Blog } from '../types';
import { useSEO, getPageSEO } from '../hooks/useSEO';
import SEOHead from '../components/Common/SEOHead';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [featuredTours, setFeaturedTours] = useState<TourType[]>([]);
  const [featuredBlogs, setFeaturedBlogs] = useState<Blog[]>([]);
  const [homePageData, setHomePageData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { seoSettings } = useSEO();

  const formatDate = (date: any) => {
    if (date && typeof date.toDate === 'function') {
      return date.toDate().toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return ''; // Return empty string or some placeholder if the date is not a valid timestamp
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tours, blogs, homePage] = await Promise.all([
          tourService.getFeaturedTours(6),
          blogService.getBlogs(),
          homePageService.getHomePageData()
        ]);
        setFeaturedTours(tours);
        setFeaturedBlogs(blogs);
        setHomePageData(homePage);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Hero slides from dynamic data or fallback
  const heroImages = homePageData?.heroSection?.slides?.filter((slide: any) => slide.isActive) || [
    {
      id: 'default',
      title: t('home.hero.default.title'),
      subtitle: t('home.hero.default.subtitle'),
      description: t('home.hero.default.description'),
      ctaText: t('home.hero.default.ctaText'),
      ctaLink: '/tours',
      backgroundImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2074&q=80',
      isActive: true
    }
  ];

  // Reset currentSlide when heroImages change
  useEffect(() => {
    if (heroImages.length > 0 && currentSlide >= heroImages.length) {
      setCurrentSlide(0);
    }
  }, [heroImages.length, currentSlide]);

  // Auto-slide effect
  useEffect(() => {
    if (heroImages.length > 0 && homePageData?.heroSection?.autoSlide) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroImages.length);
      }, homePageData?.heroSection?.slideInterval || 5000);

      return () => clearInterval(interval);
    }
  }, [heroImages.length, homePageData?.heroSection?.autoSlide, homePageData?.heroSection?.slideInterval]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  // Blog filtreleme mantığı - BlogPage ile aynı
  const filteredBlogs = featuredBlogs.filter((blog) => {
    // Yayın durumu kontrolü - daha esnek
    let isPublished = true;
    if (blog.isPublished !== undefined) {
      isPublished = blog.isPublished;
    } else if (blog.status !== undefined) {
      isPublished = blog.status === 'published' || blog.status === 'active';
    }
    
    return isPublished;
  }).slice(0, 6); // Ana sayfada sadece 6 blog göster

  // Icon mapping function
  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      Security: <Security />,
      Person: <Person />,
      AttachMoney: <AttachMoney />,
      Support: <Support />,
      People: <People />,
      Tour: <Tour />,
      LocationOn: <LocationOn />,
      Star: <Star />
    };
    return icons[iconName] || <Star />;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        backgroundColor: 'var(--navy-blue)'
      }}>
        <Typography sx={{ color: 'var(--light-gray-white)' }}>{t('loading')}</Typography>
      </Box>
    );
  }

  // SEO verilerini al
  const pageSEO = getPageSEO(seoSettings, 'home');

  return (
    <>
      <SEOHead page="home" />

      {/* Hero Section with Auto-sliding Carousel */}
      <Box
        sx={{
          position: 'relative',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'var(--navy-blue)'
        }}
      >
        {/* Background Images */}
        {heroImages.map((image: any, index: number) => (
          <Box
            key={image.id || index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `linear-gradient(rgba(1, 22, 65, 0.7), rgba(1, 22, 65, 0.7)), url(${image.backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: currentSlide === index ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
            }}
          />
        ))}

        {/* Content */}
        <Container maxWidth="lg" sx={{ 
          position: 'relative', 
          zIndex: 2, 
          height: '100%',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Box sx={{ 
            color: 'var(--light-gray-white)',
            maxWidth: { xs: '100%', md: '60%' }
          }}>
            {heroImages.length > 0 && heroImages[currentSlide] && (
              <>
                <Typography
                  variant={isMobile ? 'h3' : 'h2'}
                  component="h1"
                  gutterBottom
                  sx={{ 
                    fontWeight: 700, 
                    mb: 3,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                  }}
                >
                  {getTranslatedData(heroImages[currentSlide].title, i18n.language)}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 4, 
                    opacity: 0.9,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  {getTranslatedData(heroImages[currentSlide].subtitle, i18n.language)}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to={heroImages[currentSlide].ctaLink}
                  sx={{
                    py: 2,
                    px: 5,
                    borderRadius: 2,
                    backgroundColor: 'var(--light-blue-cta)',
                    '&:hover': {
                      backgroundColor: 'var(--cobalt-blue)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  {getTranslatedData(heroImages[currentSlide].ctaText, i18n.language)}
                </Button>
              </>
            )}
          </Box>
        </Container>

        {/* Navigation Arrows */}
        {heroImages.length > 1 && (
          <>
            <IconButton
              onClick={prevSlide}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 16,
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
              }}
            >
              <ChevronLeft fontSize="large" />
            </IconButton>
            <IconButton
              onClick={nextSlide}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 16,
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
              }}
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          </>
        )}
      </Box>

      {/* Features Section */}
      {homePageData?.featuresSection?.isActive && (
      <Box sx={{ backgroundColor: 'var(--navy-blue)', py: 5 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
            {homePageData.featuresSection.features.map((feature: any) => (
              <Box key={feature.id} sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 2 }}>
                <Paper elevation={0} sx={{
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  p: {xs: 3, md: 5},
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--light-blue-cta)',
                    mb: 3
                  }}>
                    {getIconComponent(feature.icon)}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{color: 'var(--light-gray-white)', fontWeight: 600}}>
                    {getTranslatedData(feature.title, i18n.language)}
                  </Typography>
                  <Typography variant="body2" sx={{color: 'var(--light-gray-white)', opacity: 0.8}}>
                    {getTranslatedData(feature.description, i18n.language)}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
      )}

      {/* About Us Section */}
      {homePageData?.aboutUsSection?.isActive && (
        <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'var(--light-gray-white)' }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mx: -4 }}>
              <Box sx={{ width: { xs: '100%', md: '50%' }, p: 4 }}>
                <Typography variant="body1" sx={{ color: 'var(--light-blue-cta)', fontWeight: 600, mb: 2 }}>
                  {getTranslatedData(homePageData.aboutUsSection.subtitle, i18n.language)}
                </Typography>
                <Typography variant="h3" component="h2" sx={{ fontWeight: 700, color: 'var(--navy-blue)', mb: 4 }}>
                  {getTranslatedData(homePageData.aboutUsSection.title, i18n.language)}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 4 }}>
                  {getTranslatedData(homePageData.aboutUsSection.description, i18n.language)}
                </Typography>
                <Button 
                  variant="contained" 
                  component={Link} 
                  to="/about"
                  size="large"
                  sx={{
                    backgroundColor: 'var(--cobalt-blue)',
                    py: 1.5,
                    px: 4,
                    '&:hover': {
                      backgroundColor: 'var(--navy-blue)'
                    }
                  }}
                >
                  {getTranslatedData(homePageData.aboutUsSection.ctaText, i18n.language) || t('home.aboutUs.cta')}
                </Button>
              </Box>
              <Box sx={{ width: { xs: '100%', md: '50%' }, p: 4 }}>
                <Box sx={{ 
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
                  height: 400
                }}>
                  <img
                    src={homePageData.aboutUsSection.image}
                    alt={getTranslatedData(homePageData.aboutUsSection.title, i18n.language)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Featured Tours Section */}
      {homePageData?.featuredToursSection?.isActive && featuredTours.length > 0 && (
        <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'var(--navy-blue)' }}>
          <Container maxWidth="lg">
            <Typography variant="h3" component="h2" align="center" sx={{ mb: 8, fontWeight: 700, color: 'var(--light-gray-white)' }}>
              {getTranslatedData(homePageData.featuredToursSection.title, i18n.language) || t('home.featuredTours.title')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
              {featuredTours.map((tour) => (
                <Box key={tour.id} sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' }, p: 2 }}>
                  <Card
                    component={Link}
                    to={`/tours/${tour.id}`}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      color: 'inherit',
                      backgroundColor: 'white',
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={tour.images[0] || 'https://source.unsplash.com/random/800x600?nature,travel'}
                      alt={getTranslatedData(tour.name, i18n.language)}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Chip
                          icon={<LocationOn sx={{ fontSize: 16 }} />}
                          label={getTranslatedData(tour.destination, i18n.language)}
                          size="small"
                          sx={{ backgroundColor: 'var(--light-gray)', color: 'var(--navy-blue)' }}
                        />
                         <Chip
                          icon={<AccessTime sx={{ fontSize: 16 }} />}
                          label={`${tour.duration} ${t('days', { count: tour.duration })}`}
                          size="small"
                           sx={{ backgroundColor: 'var(--light-gray)', color: 'var(--navy-blue)' }}
                        />
                      </Box>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'var(--navy-blue)', mb: 2 }}>
                        {getTranslatedData(tour.name, i18n.language)}
                      </Typography>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Typography variant="h5" color="var(--light-blue-cta)" fontWeight="bold">
                          {tour.price.toLocaleString('de-DE')} ₺
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      )}
      
      {/* Testimonials Section */}
      {homePageData?.testimonialsSection?.isActive && (
        <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: 'var(--navy-blue)',
                  mb: 2
                }}
              >
                {getTranslatedData(homePageData.testimonialsSection.title, i18n.language) || t('home.testimonials.title')}
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 400
                }}
              >
                {getTranslatedData(homePageData.testimonialsSection.subtitle, i18n.language)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
              {homePageData.testimonialsSection.testimonials
                ?.filter((testimonial: any) => testimonial.isActive)
                .sort((a: any, b: any) => a.order - b.order)
                .map((testimonial: any) => (
                  <Box key={testimonial.id} sx={{ width: { xs: '100%', md: '33.33%' }, p: 2 }}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        borderRadius: 3,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                      }}
                    >
                      <CardContent sx={{ p: {xs: 3, md: 4}, textAlign: 'center' }}>
                        <Avatar 
                          src={testimonial.avatar} 
                          alt={getTranslatedData(testimonial.author, i18n.language) || getTranslatedData(testimonial.name, i18n.language)} 
                          sx={{ width: 80, height: 80, margin: '0 auto 16px' }}
                        />
                        <Box sx={{ mb: 2 }}>
                          <Rating name="read-only" value={testimonial.rating} readOnly />
                        </Box>
                        <FormatQuote sx={{ color: 'var(--light-blue-cta)', fontSize: 40, mb: 1 }} />
                        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 3, fontSize: '1.1rem' }}>
                          "{getTranslatedData(testimonial.quote, i18n.language) || getTranslatedData(testimonial.comment, i18n.language)}"
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--navy-blue)' }}>
                          {getTranslatedData(testimonial.author, i18n.language) || getTranslatedData(testimonial.name, i18n.language)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {getTranslatedData(testimonial.location, i18n.language)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Featured Blog Section */}
      {homePageData?.blogSection?.isActive && (
        <Box sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              align="center"
              sx={{ 
                fontWeight: 700, 
                mb: 5,
                color: 'var(--navy-blue)',
              }}
            >
              {getTranslatedData(homePageData.blogSection.title, i18n.language) || t('home.blog.title')}
            </Typography>
            {homePageData.blogSection.subtitle && (
              <Typography 
                variant="h6" 
                align="center"
                sx={{ 
                  color: 'text.secondary',
                  mb: 5,
                  fontWeight: 400
                }}
              >
                {getTranslatedData(homePageData.blogSection.subtitle, i18n.language) || t('home.blog.subtitle')}
              </Typography>
            )}

            {filteredBlogs.length > 0 ? (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
                  {filteredBlogs.map((blog) => (
                    <Box key={blog.id} sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' }, p: 2 }}>
                      <Card
                        component={Link}
                        to={`/blog/${blog.id}`}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          textDecoration: 'none',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={blog.featuredImage || 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1170&q=80'}
                          alt={getTranslatedData(blog.title, i18n.language)}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                            {blog.category && (
                              <Chip
                                label={getTranslatedData(blog.category, i18n.language)}
                                size="small"
                                sx={{ 
                                  mb: 2, 
                                  backgroundColor: 'var(--light-blue-cta)', 
                                  color: 'white',
                                  fontWeight: 500
                                }}
                              />
                            )}
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, color: 'var(--navy-blue)' }}>
                              {getTranslatedData(blog.title, i18n.language)}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                              {getTranslatedData(blog.excerpt, i18n.language) || (getTranslatedData(blog.content, i18n.language) && getTranslatedData(blog.content, i18n.language).substring(0, 150) + '...') || t('home.blog.noDescription')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 1 }}>
                              <CalendarToday sx={{ mr: 1, fontSize: '1rem' }} />
                              <Typography variant="caption">
                                {formatDate(blog.createdAt)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 1, ml: 2 }}>
                              <Person sx={{ mr: 1, fontSize: '1rem' }} />
                              <Typography variant="caption">
                                {blog.author || t('home.blog.authorAdmin')}
                              </Typography>
                            </Box>
                            <Button 
                              variant="contained" 
                              component={Link} 
                              to={`/blog/${blog.id}`} 
                              sx={{
                                backgroundColor: '#005BBD',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#004A9F',
                                },
                              }}
                            >
                              {t('home.blog.readMore')}
                            </Button>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
             
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <Button
                    component={Link}
                    to="/blog"
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#005BBD',
                      color: '#005BBD',
                      '&:hover': {
                        borderColor: '#005BBD',
                        backgroundColor: '#005BBD',
                        color: 'white',
                      },
                    }}
                  >
                    {t('home.blog.seeAll')}
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
                  {t('home.blog.comingSoon')}
                </Typography>
                <Button
                  component={Link}
                  to="/blog"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: '#005BBD',
                    color: '#005BBD',
                    '&:hover': {
                      borderColor: '#005BBD',
                      backgroundColor: '#005BBD',
                      color: 'white',
                    },
                  }}
                >
                  {t('home.blog.visitBlogPage')}
                </Button>
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* CTA Section */}
      {homePageData?.ctaSection?.isActive && (
        <Box sx={{ py: { xs: 8, md: 15 }, backgroundColor: 'var(--cobalt-blue)', color: 'white' }}>
          <Container maxWidth="md">
            <Typography variant="h3" component="h2" align="center" sx={{ mb: 3, fontWeight: 700 }}>
              {getTranslatedData(homePageData.ctaSection.title, i18n.language)}
            </Typography>
            <Typography variant="h6" align="center" sx={{ mb: 5, opacity: 0.9, maxWidth: '80%', mx: 'auto' }}>
              {getTranslatedData(homePageData.ctaSection.subtitle, i18n.language)}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button 
                variant="contained" 
                size="large" 
                component={Link} 
                to={homePageData.ctaSection.primaryCtaLink || '/tours'}
                sx={{
                  backgroundColor: 'white',
                  color: 'var(--cobalt-blue)',
                  '&:hover': {
                    backgroundColor: 'var(--light-gray)'
                  }
                }}
              >
                {getTranslatedData(homePageData.ctaSection.primaryCtaText, i18n.language)}
              </Button>
            </Box>
          </Container>
        </Box>
      )}
    </>
  );
};

export default HomePage; 